import { Check, ChevronsUpDown, PencilLine } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { OverflowDisplayTooltip } from "../Tooltip/OverflowDisplayTooltip";

import { MODEL_OPTION_ITEM_CLASS, PANEL_FALLBACK_WIDTH } from "./constants";
import {
  getBridgeMetrics,
  getHoverAnchor,
  getPanelHeight,
  getPanelLeft,
  getPanelTop,
  isCursorOverPortal,
  isOverHoverAnchor,
  PanelSide,
  resolvePanelPlacement,
  useStripInert,
} from "./utils";

import { ModelDetailPanel } from "@/components/ModelDropdown/ModelDetailPanel";
import { formatContextLength, formatPrice } from "@/components/ModelDropdown/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ModelMetadataEntry } from "@/queries/models/useModelMetadata";

interface SelectModelDropdownProps {
  model: string | null;
  setModel: (model: string | null) => void;
  options: string[];
  /**
   * Optional capability groups (e.g. Chat / Image / Video). When provided, the
   * list is rendered as labelled sections instead of one flat list. `options`
   * is still used for search-match / custom-model detection.
   */
  optionGroups?: { label: string; options: string[] }[];
  width?: string;
  size?: "base" | "lg";
  isLoading?: boolean;
  disabled?: boolean;
  modelMetadata?: Record<string, ModelMetadataEntry | null>;
  provider?: string;
}

const MetadataBadges = ({ meta }: { meta: ModelMetadataEntry }) => {
  const ctx = formatContextLength(meta.context_length);
  const inputPrice = formatPrice(meta.price_per_1m_tokens_input);
  const outputPrice = formatPrice(meta.price_per_1m_tokens_output);
  const hasPricing = inputPrice !== null || outputPrice !== null;

  return (
    <span className="ml-auto flex shrink-0 items-center gap-1 pl-2">
      {ctx && (
        <span className="rounded bg-gray-100 px-1 py-0.5 text-[10px] leading-tight text-gray-500">
          {ctx} context
        </span>
      )}
      {hasPricing && (
        <span className="rounded bg-blue-50 px-1 py-0.5 text-[10px] leading-tight text-blue-600">
          {inputPrice ?? "?"}/{outputPrice ?? "?"}
        </span>
      )}
    </span>
  );
};

export const SelectModelDropdown = ({
  model,
  setModel,
  options,
  optionGroups,
  width = "[200px]",
  size = "base",
  isLoading,
  disabled,
  modelMetadata,
  provider,
}: SelectModelDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const isExistingValue = options.includes(value);

  const [hoveredModel, setHoveredModel] = useState<string | null>(null);
  const [hoverAnchor, setHoverAnchor] = useState<ReturnType<typeof getHoverAnchor> | null>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const [panelSide, setPanelSide] = useState<PanelSide>("right");
  const onPanelRef = useRef(false);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const panelElRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef(0);
  useStripInert(portalRef);

  const hidePanel = useCallback(() => {
    onPanelRef.current = false;
    setHoveredModel(null);
    setHoverAnchor(null);
  }, []);

  const isOverPortal = useCallback(
    (x: number, y: number) => isCursorOverPortal(portalRef.current, x, y),
    [],
  );

  useEffect(() => {
    if (!hoveredModel || !hoverAnchor) {
      return;
    }

    const onMove = (e: MouseEvent) => {
      const hit =
        isOverPortal(e.clientX, e.clientY) || isOverHoverAnchor(e.clientX, e.clientY, hoverAnchor);
      const was = onPanelRef.current;
      onPanelRef.current = hit;
      if (was && !hit) {
        hidePanel();
      }
    };

    document.addEventListener("mousemove", onMove, true);
    return () => document.removeEventListener("mousemove", onMove, true);
  }, [hoveredModel, hoverAnchor, hidePanel, isOverPortal]);

  const onItemLeave = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!onPanelRef.current) {
        hidePanel();
      }
    });
  }, [hidePanel]);

  const onItemEnter = useCallback((e: React.MouseEvent<HTMLDivElement>, name: string) => {
    cancelAnimationFrame(rafRef.current);
    onPanelRef.current = false;

    const nextHoverAnchor = getHoverAnchor(e.currentTarget.getBoundingClientRect());
    const initialPlacement = resolvePanelPlacement(
      nextHoverAnchor,
      PANEL_FALLBACK_WIDTH,
      window.innerWidth,
    );

    setHoverAnchor(nextHoverAnchor);
    setPanelSide(initialPlacement.side);
    setPanelStyle({ top: nextHoverAnchor.itemTop, left: initialPlacement.left });
    setHoveredModel(name);
  }, []);

  useLayoutEffect(() => {
    const panelElement = panelElRef.current;
    if (!panelElement || !hoverAnchor) {
      return;
    }

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const panelHeight = panelElement.scrollHeight;
    const panelWidth = panelElement.offsetWidth || PANEL_FALLBACK_WIDTH;
    const { left: resolvedLeft, side: resolvedSide } = resolvePanelPlacement(
      hoverAnchor,
      panelWidth,
      viewportWidth,
    );
    const resolvedTop = getPanelTop(hoverAnchor, panelHeight, viewportHeight);

    setPanelSide(resolvedSide);
    setPanelStyle({ top: resolvedTop, left: resolvedLeft });
  }, [hoverAnchor, hoveredModel]);

  const preventPanelDismiss = useCallback(
    (e: { target: EventTarget | null; preventDefault: () => void }) => {
      if (portalRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    },
    [],
  );

  const hoveredMeta = hoveredModel ? (modelMetadata?.[hoveredModel] ?? null) : null;
  const showPanel = open && hoveredModel && hoveredMeta && hoverAnchor;
  const panelLeft = getPanelLeft(panelStyle, hoverAnchor);
  const panelTop =
    typeof panelStyle.top === "number" ? panelStyle.top : (hoverAnchor?.itemTop ?? 0);
  const panelWidth = panelElRef.current?.offsetWidth ?? PANEL_FALLBACK_WIDTH;
  const panelHeight = getPanelHeight(panelElRef.current);
  const bridgeMetrics = getBridgeMetrics({
    hoverAnchor,
    panelSide,
    panelLeft,
    panelTop,
    panelWidth,
    panelHeight,
  });

  const renderModelItem = (m: string) => {
    const meta = modelMetadata?.[m];
    return (
      <CommandItem
        key={m}
        onSelect={() => {
          setOpen(false);
          setModel(m);
        }}
        onMouseEnter={meta ? e => onItemEnter(e, m) : onItemLeave}
        onMouseLeave={onItemLeave}
        className={MODEL_OPTION_ITEM_CLASS}
      >
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check className={cn("h-4 w-4", model === m ? "opacity-100" : "opacity-0")} />
        </span>
        <span className="flex min-w-0 flex-1 items-center">
          <OverflowDisplayTooltip side="right" content={m}>
            {m}
          </OverflowDisplayTooltip>
          {meta && <MetadataBadges meta={meta} />}
        </span>
      </CommandItem>
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger disabled={isLoading || disabled} asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent bg-white px-3 py-2 text-sm shadow-sm ring-offset-background hover:bg-white focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground",
              `w-${width}`,
              size === "lg" && "font-base py-2 pl-4 pr-2 text-sm",
            )}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="my-1 h-3 w-[156px] animate-pulse rounded bg-gray-200" />
              </div>
            ) : (
              <>
                <OverflowDisplayTooltip content={model}>
                  {model ?? "Select model..."}
                </OverflowDisplayTooltip>
                <ChevronsUpDown className="h-4 w-4 opacity-50" />
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "relative z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            `w-${width}`,
            "p-0 px-2",
          )}
          onPointerDownOutside={preventPanelDismiss}
          onInteractOutside={preventPanelDismiss}
          onWheelCapture={e => e.stopPropagation()}
          onTouchMoveCapture={e => e.stopPropagation()}
        >
          <Command>
            <CommandInput
              placeholder={
                width === "full" ? "Search or enter fine-tuned model..." : "Enter a model..."
              }
              value={value}
              onValueChange={setValue}
              className="px-1 focus:ring-0"
            />
            <CommandEmpty>No models found.</CommandEmpty>
            <div
              className="max-h-[calc(40vh)] overflow-y-auto overscroll-contain"
              data-scroll-lock-scrollable
              onWheelCapture={e => e.stopPropagation()}
              onTouchMoveCapture={e => e.stopPropagation()}
            >
              {optionGroups && optionGroups.length > 0 ? (
                optionGroups.map(group => (
                  <CommandGroup key={group.label} heading={group.label}>
                    {group.options.map(renderModelItem)}
                  </CommandGroup>
                ))
              ) : (
                <CommandGroup>{options.map(renderModelItem)}</CommandGroup>
              )}
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    setModel(value);
                  }}
                  onMouseEnter={onItemLeave}
                  className={cn(
                    MODEL_OPTION_ITEM_CLASS,
                    "italic",
                    isExistingValue ? "opacity-0" : "opacity-100",
                  )}
                >
                  <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Check
                      className={cn("h-4 w-4", model === value ? "opacity-100" : "opacity-0")}
                    />
                  </span>
                  {value && !isExistingValue ? <PencilLine className="mr-2 h-4 w-4" /> : null}
                  {value}
                </CommandItem>
              </CommandGroup>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {showPanel &&
        createPortal(
          <div
            ref={portalRef}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 9998,
              pointerEvents: "none",
            }}
          >
            {/* Transparent bridge between dropdown item and panel */}
            <div
              style={{
                position: "fixed",
                top: bridgeMetrics.top,
                left: bridgeMetrics.left,
                width: bridgeMetrics.width,
                height: bridgeMetrics.height,
                pointerEvents: "auto",
              }}
            />
            <div
              ref={panelElRef}
              className="border border-gray-200 bg-white shadow-xl duration-150 animate-in fade-in-0 zoom-in-95"
              style={{
                position: "fixed",
                ...panelStyle,
                borderRadius: 8,
                overflow: "hidden",
                zIndex: 9999,
                pointerEvents: "auto",
              }}
            >
              <ModelDetailPanel
                metadata={hoveredMeta}
                modelName={hoveredModel}
                provider={provider}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
